import { BookOpen, Video, Images, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHeading, FinalCTA } from "@/components/shared";
import { business, images, routes } from "@/lib/site";
import { combine, organizationSchema, breadcrumbSchema } from "@/lib/schema";

const categories = [
  { icon: BookOpen, title: "Blogs", desc: "Educational articles on chiropractic awareness, pain management, posture tips and spine health.", img: images.spine },
  { icon: Video, title: "Videos", desc: "Doctor-led educational videos, patient awareness clips and short chiropractic information.", img: images.adjustment },
  { icon: Images, title: "Gallery", desc: "Clinic images, professional moments, awareness activities and events.", img: images.clinic },
];

export default function Resources() {
  const jsonLd = combine(
    organizationSchema,
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "Resources", path: routes.resources },
    ])
  );

  return (
    <>
      <SEO
        path={routes.resources}
        title="Resources — Blogs, Videos & Gallery | Dr. Satyam Jaiswal Varanasi"
        description="Explore chiropractic resources from Dr. Satyam Jaiswal in Varanasi — educational blogs, awareness videos and clinic gallery. New content added regularly."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Resources"
        title="Learn, Watch &amp; Explore"
        sub="Educational content to help you understand chiropractic care, pain management and healthy movement. We're adding new resources regularly."
        breadcrumb={[{ label: "Resources" }]}
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="group overflow-hidden rounded-2xl bg-soft shadow-sm" data-testid={`resource-card-${c.title.toLowerCase()}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={c.img} alt={c.title} loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-navy/45" />
                    <div className="absolute bottom-4 left-4 grid h-11 w-11 place-items-center rounded-xl bg-brand-orange">
                      <c.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-heading font-bold text-navy text-xl">{c.title}</h3>
                    <p className="mt-2 text-ink leading-relaxed text-sm">{c.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy/5 px-4 py-2 text-xs font-bold uppercase tracking-wide text-navy/60">
                      Coming Soon
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Follow us */}
          <Reveal>
            <div className="mt-16 rounded-3xl bg-navy p-10 lg:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 hero-glow" />
              <div className="relative">
                <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl">Follow Along Until Then</h2>
                <p className="mt-3 text-white/70 max-w-xl mx-auto">
                  Watch educational clips and awareness content on our social channels.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <a href={business.socials.youtube} target="_blank" rel="noopener noreferrer" data-testid="resources-youtube" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-navy hover:bg-brand-orange hover:text-white transition-colors">
                    <Youtube className="h-5 w-5" /> YouTube <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <a href={business.socials.instagram} target="_blank" rel="noopener noreferrer" data-testid="resources-instagram" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-navy hover:bg-brand-orange hover:text-white transition-colors">
                    <Instagram className="h-5 w-5" /> Instagram <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
